// =============================================================================
// ui/tabs/JournalVisibility.tsx — the gear that hides and shows Journal sections.
// -----------------------------------------------------------------------------
// Josh, 2026-10-06 (TCR-81): *"I'd like to see a little gear icon that allows the
// user to show/hide from a list rather than have to click a button within each
// element and then go and find the section at the bottom of the page to restore
// them, if needed."*
//
// So this replaces BOTH halves of beta's design — the ⊘ button inside every
// hideable card and the `#jp-hidden-tray` restore strip at the foot of the page.
// Hiding and restoring happen in the same place, which is the point.
//
// ── 🔴 WHY IT IS A PORTALLED DRAWER AND NOT A POPOVER (TCR-82) ──
//
// The first version was an absolutely-positioned panel under the gear. It
// rendered BEHIND the cards below it and was cut off — Josh's screenshot.
//
// `app.css` carries `.journal-phased > * { position: relative; z-index: 1; }`, so
// every direct child of the phased Journal gets its OWN STACKING CONTEXT. The
// panel's `z-index: 40` only ordered it inside `.jp-vis-row`; against its
// siblings that whole row is just `z-index: 1`, and the glance tiles and section
// cards come later in the DOM, so they paint on top.
//
// 🔴 A `position: fixed` DRAWER IN THE SAME PLACE WOULD BE TRAPPED IDENTICALLY.
// Fixed escapes the scroll box, NOT the stacking context — a fixed element inside
// a `z-index: 1` child still paints within that child's layer. Raising the number
// does nothing either; the number was never the problem.
//
// ▶ So the drawer is rendered through a PORTAL to `document.body`, outside
// `.journal-phased` entirely. That is also why `FilterDrawer` never hit this: it
// lives in the shell, so it was never inside the trap.
//
// ── ✅ IT REUSES THE FILTER DRAWER'S CLASSES, NOT A SECOND SET ──
//
// `.drawer-scrim`, `.filter-drawer`, `.filter-drawer-head`, `.filter-drawer-body`.
// None of those rules mentions filters — only their names do. They are already
// fixed, full height, right-anchored and `width: min(380px, 92vw)` with a
// scrolling body, which is exactly the "consistent experience" Josh asked for.
// ⚠ `FilterDrawer.tsx` is deliberately untouched: it works, and refactoring a
// working surface to share a shell is a separate decision from fixing this one.
//
// ── ⚠ NOT COLLAPSE ──
//
// `JournalSection` already folds on a header click and is ephemeral by design. A
// hidden section stays gone until it is restored HERE. The gear therefore lives
// in the phase header rather than inside a section header, so a click aimed at it
// can never be read as a fold.
// =============================================================================

import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useJournalHidden } from '../state/useJournalHidden';
import type { HidePhase } from '../../selectors/journalHidden';
import { Icon } from '../shell/Icon';

export function JournalVisibility({ phase }: { phase: HidePhase }) {
  const { rows, hidden, canWrite, busy, error, setVisible } = useJournalHidden(phase);
  const [open, setOpen] = useState(false);

  // ⚠ Escape closes it, as it does the filter drawer. A modal that can only be
  // dismissed with the mouse is a trap for anyone navigating by keyboard.
  //
  // ⚠ THE CLICK-OUTSIDE LISTENER IS GONE, not kept alongside this. The scrim now
  // covers the page and closes on click, so a document-level `mousedown` handler
  // would be a second mechanism doing the same job — and one that could fire on
  // a drag that merely ENDED outside the drawer.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  /**
   * ⚠ THIS PHASE'S HIDDEN COUNT, NOT THE WHOLE STORED LIST (TCR-82). With one
   * drawer per phase, a global count would tell a trader on Pre-Market that three
   * things are hidden when all three are on the Recap — which is the same
   * "which screen does this belong to" confusion that split the drawers.
   *
   * ⚠ Counted over `rows`, so a stray is included wherever it is offered; it IS
   * hidden, and the badge is the only hint that anything is.
   */
  const count = rows.filter((u) => hidden.includes(u.id)).length;
  const phaseLabel = phase === 'pre' ? 'Pre-Market' : 'Recap';

  return (
    <div className="jp-vis-wrap">
      <button
        type="button"
        className={`btn btn-icon jp-vis-btn${count ? ' has-hidden' : ''}`}
        aria-expanded={open}
        aria-label={count
          ? `Show or hide ${phaseLabel} sections — ${count} hidden`
          : `Show or hide ${phaseLabel} sections`}
        title={`Show or hide ${phaseLabel} sections`}
        onClick={() => setOpen((v) => !v)}
      >
        <Icon name="settings" />
        {/* ⚠ THE COUNT IS THE WHOLE REPLACEMENT FOR BETA'S TRAY. Without it a
            hidden section is invisible AND so is the fact that anything is
            hidden — a trader who forgets would conclude the app lost a feature,
            which is the complaint this control exists to answer. */}
        {count ? <span className="jp-vis-count">{count}</span> : null}
      </button>

      {/* 🔴 PORTALLED, AND UNMOUNTED WHEN CLOSED — two separate reasons.
          THE PORTAL is what escapes `.journal-phased`'s per-child stacking
          context; see the header. THE UNMOUNT is from TCR-81: the section labels
          duplicate the real field labels, so a body that rendered while shut put
          every one of them in the accessibility tree and made three existing
          Journal tests ambiguous ("Found multiple elements with the text: Things
          I did well"). Unlike the sections themselves, this panel holds no
          writing, so there is nothing to lose by unmounting it. */}
      {open ? createPortal(
        <>
          <div className="drawer-scrim" onClick={() => setOpen(false)} aria-hidden="true" />
          <aside
            className="filter-drawer jp-vis-drawer"
            role="dialog" aria-modal="true" aria-label={`${phaseLabel} sections`}
          >
            <header className="filter-drawer-head">
              <b>Show or hide · {phaseLabel}</b>
              <button type="button" className="btn" onClick={() => setOpen(false)}>Close</button>
            </header>

            <div className="filter-drawer-body">
              <p className="muted jp-vis-hint">
                Untick a section to hide it everywhere in the Journal. Nothing you have
                written is deleted — hidden sections keep their contents and come back
                exactly as they were.
              </p>

              {!canWrite ? (
                <p role="alert" className="muted jp-vis-hint">
                  Your settings haven’t loaded yet, so this can’t be changed.
                </p>
              ) : null}

              {error ? <p role="alert" className="import-alert">{error}</p> : null}

              <div className="ctag-list">
                {rows.map((u) => {
                  const visible = !hidden.includes(u.id);
                  /**
                   * ⚠ A CHILD WHOSE PARENT IS HIDDEN IS SHOWN TICKED-BUT-INERT,
                   * not silently unticked (TCR-82). Josh asked for the three
                   * Market Read cards nested under their section, which makes
                   * "section hidden, card still ticked" reachable.
                   *
                   * 🔴 THE CHILD'S STORED KEY IS NEVER REWRITTEN. Those three are
                   * BETA'S keys and beta is still writing them — clearing one
                   * here to tidy the display would change what the other client
                   * sees. So the parent governs the SCREEN, and the child keeps
                   * its own answer for when the parent comes back.
                   */
                  const parentHidden = !!u.parent && hidden.includes(u.parent);
                  return (
                    <label
                      key={u.id}
                      className={`ctag-row tabvis-row${visible ? ' checked' : ''}`
                        + (u.parent ? ' jp-vis-child' : '')
                        + (parentHidden ? ' jp-vis-muted' : '')}
                      title={parentHidden
                        ? 'Its section is hidden, so this is hidden too'
                        : undefined}
                    >
                      <input
                        type="checkbox"
                        checked={visible}
                        disabled={!canWrite || busy}
                        aria-label={`Show ${u.label}`}
                        onChange={(e) => { void setVisible(u.id, e.target.checked); }}
                      />
                      <span className="tabvis-text">
                        <b>{u.label}</b>
                        <span className="muted tabvis-desc">{u.description}</span>
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>
          </aside>
        </>,
        document.body,
      ) : null}
    </div>
  );
}
